import React from "react";
import { createPortal } from "react-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../components/feedback/ToastProvider";

import {
  AlertTriangle,
  BadgeCheck,
  Building2,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleX,
  Clock3,
  ExternalLink,
  Globe2,
  Mail,
  MapPin,
  MessageSquareText,
  Phone,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRound,
  Wrench,
  X,
  XCircle,
  KeyRound,
  CircleCheckBig,
  TimerOff,
  BriefcaseBusiness,
} from "lucide-react";

import "./AdminRequestsPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   OPTIONS / ENUM MAPPING
   ========================================================= */

const STATUS_OPTIONS = [
  { value: "ALL", key: "all", fallback: "All statuses" },
  { value: "PENDING", key: "pending", fallback: "Pending" },
  { value: "APPROVED", key: "approved", fallback: "Approved" },
  { value: "CODE_SENT", key: "codeSent", fallback: "Access code sent" },
  { value: "COMPLETED", key: "completed", fallback: "Completed" },
  { value: "REJECTED", key: "rejected", fallback: "Rejected" },
  { value: "EXPIRED", key: "expired", fallback: "Expired" },
];

const ORDERING_OPTIONS = [
  { value: "NEWEST", key: "newest", fallback: "Newest first" },
  { value: "OLDEST", key: "oldest", fallback: "Oldest first" },
  { value: "COMPANY_ASC", key: "companyAsc", fallback: "Organisation A–Z" },
  { value: "COMPANY_DESC", key: "companyDesc", fallback: "Organisation Z–A" },
];

const REJECTION_REASON_OPTIONS = [
  {
    value: "INCOMPLETE_INFORMATION",
    key: "incompleteInformation",
    fallback: "Incomplete information",
  },
  {
    value: "INVALID_ORGANISATION_DETAILS",
    key: "invalidOrganisationDetails",
    fallback: "Invalid organisation details",
  },
  {
    value: "OUTSIDE_SUPPORTED_REGION",
    key: "outsideSupportedRegion",
    fallback: "Outside supported region",
  },
  {
    value: "DUPLICATE_REQUEST",
    key: "duplicateRequest",
    fallback: "Duplicate request",
  },
  {
    value: "ELIGIBILITY_REQUIREMENTS_NOT_MET",
    key: "eligibilityRequirementsNotMet",
    fallback: "Eligibility requirements not met",
  },
  {
    value: "OTHER",
    key: "other",
    fallback: "Other",
  },
];

/* =========================================================
   HELPERS
   ========================================================= */

function getInitials(value) {
  if (!value) return "—";

  return value
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatCountry(code, t) {
  if (!code) return "";

  const fallback = {
    BE: "Belgium",
    ES: "Spain",
    PT: "Portugal",
    LU: "Luxembourg",
  };

  return t(`countries.${code}`, fallback[code] || code);
}

function formatDateTime(value, locale) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getApplicantName(request) {
  return [request?.first_name, request?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
}

function getOssType(request, t) {
  if (request?.is_advisory && request?.is_turnkey_renovation) {
    return t("ossTypes.advisoryAndTurnkey", "Advisory & Turnkey Renovation");
  }

  if (request?.is_advisory) {
    return t("ossTypes.advisory", "Advisory");
  }

  if (request?.is_turnkey_renovation) {
    return t("ossTypes.turnkey", "Turnkey Renovation");
  }

  return t("common.notSpecified", "Not specified");
}

function getCoverage(request) {
  return Array.isArray(request?.coverage_postal_codes)
    ? request.coverage_postal_codes.filter(Boolean)
    : [];
}

function formatCoverage(request, t) {
  const codes = getCoverage(request);

  if (!codes.length) {
    return t("common.notSpecified", "Not specified");
  }

  if (codes.length <= 5) {
    return codes.join(", ");
  }

  return `${codes.slice(0, 5).join(", ")} +${codes.length - 5}`;
}

function getStatusMeta(status, t) {
  switch (status) {
    case "APPROVED":
      return {
        key: "approved",
        label: t("status.approved", "Approved"),
        icon: BadgeCheck,
      };

    case "REJECTED":
      return {
        key: "rejected",
        label: t("status.rejected", "Rejected"),
        icon: XCircle,
      };

    case "CODE_SENT":
      return {
        key: "code-sent",
        label: t("status.codeSent", "Access code sent"),
        icon: KeyRound,
      };

    case "COMPLETED":
      return {
        key: "completed",
        label: t("status.completed", "Completed"),
        icon: CircleCheckBig,
      };

    case "EXPIRED":
      return {
        key: "expired",
        label: t("status.expired", "Expired"),
        icon: TimerOff,
      };

    default:
      return {
        key: "pending",
        label: t("status.pending", "Pending"),
        icon: Clock3,
      };
  }
}

function getRejectionReasonLabel(code, t) {
  if (!code) return "";

  const option = REJECTION_REASON_OPTIONS.find((item) => item.value === code);

  if (!option) return code;

  return t(`rejectionReasons.${option.key}`, option.fallback);
}

function scrollSmoothlyToTop() {
  window.requestAnimationFrame(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  });
}

/* =========================================================
   STATUS BADGE
   ========================================================= */

function StatusBadge({ status, t }) {
  const meta = getStatusMeta(status, t);
  const Icon = meta.icon;

  return (
    <span
      className={`admin-requests-status admin-requests-status--${meta.key}`}
    >
      <Icon size={13} />
      {meta.label}
    </span>
  );
}

/* =========================================================
   REQUEST ROW
   ========================================================= */

function RequestRow({ request, selected, locale, t, onSelect }) {
  const applicantName =
    getApplicantName(request) ||
    request.email ||
    t("common.unknownApplicant", "Unknown applicant");

  return (
    <article
      className={`admin-requests-row ${selected ? "is-selected" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(request.id)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(request.id);
        }
      }}
    >
      <div className="admin-requests-row__top">
        <div className="admin-requests-row__identity">
          <span className="admin-requests-row__avatar">
            {getInitials(request.company_name)}
          </span>

          <div>
            <strong>{request.company_name}</strong>

            <span>
              {applicantName} · {formatCountry(request.country_code, t)}
            </span>
          </div>
        </div>

        <StatusBadge status={request.status} t={t} />
      </div>

      <div className="admin-requests-row__meta">
        <span>
          <MapPin size={14} />
          {formatCoverage(request, t)}
        </span>

        <span>
          <Wrench size={14} />
          {getOssType(request, t)}
        </span>

        <span>
          <CalendarDays size={14} />
          {formatDateTime(request.created_at, locale)}
        </span>
      </div>

      <ChevronRight size={18} className="admin-requests-row__chevron" />
    </article>
  );
}

/* =========================================================
   DETAIL ROW
   ========================================================= */

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="admin-requests-detail-row">
      <span className="admin-requests-detail-row__icon">
        <Icon size={16} />
      </span>

      <span className="admin-requests-detail-row__label">{label}</span>

      <strong>{children || "—"}</strong>
    </div>
  );
}

/* =========================================================
   EMPTY DETAILS
   ========================================================= */

function EmptyDetails({ t }) {
  return (
    <div className="admin-requests-empty-details">
      <span className="admin-requests-empty-details__icon">
        <Building2 size={28} />
      </span>

      <strong>{t("details.emptyTitle", "Select a request")}</strong>

      <p>
        {t(
          "details.emptyText",
          "Choose an OSS join request from the list to review its details.",
        )}
      </p>
    </div>
  );
}

/* =========================================================
   LOADING
   ========================================================= */

function RequestsLoading() {
  return (
    <div className="admin-requests-loading">
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="admin-requests-loading__row" />
      ))}
    </div>
  );
}

/* =========================================================
   PAGE
   ========================================================= */

export default function AdminRequestsPage() {
  const { getAccessTokenSilently } = useAuth0();

  const { t, i18n } = useTranslation("admin-requests");

  const { showToast } = useToast();

  const [requests, setRequests] = React.useState([]);

  const [selectedId, setSelectedId] = React.useState(null);

  const [loading, setLoading] = React.useState(true);

  const [refreshing, setRefreshing] = React.useState(false);

  const [error, setError] = React.useState("");

  const [query, setQuery] = React.useState("");

  const [statusFilter, setStatusFilter] = React.useState("ALL");

  const [ordering, setOrdering] = React.useState("NEWEST");

  const [approveModalOpen, setApproveModalOpen] = React.useState(false);

  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);

  const [actionLoading, setActionLoading] = React.useState(false);

  const [approvalMessage, setApprovalMessage] = React.useState("");

  const [rejectionReason, setRejectionReason] = React.useState("");

  const [rejectionNote, setRejectionNote] = React.useState("");

  const locale =
    i18n.language === "nl"
      ? "nl-BE"
      : i18n.language === "es"
        ? "es-ES"
        : i18n.language === "ca"
          ? "ca-ES"
          : "en-GB";

  /* =====================================================
     LOAD REQUESTS
     ===================================================== */

  const loadRequests = React.useCallback(
    async ({ background = false } = {}) => {
      try {
        if (background) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/admin/oss-join-requests/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            payload.detail ||
              t("errors.load", "Could not load OSS join requests."),
          );
        }

        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload.results)
            ? payload.results
            : Array.isArray(payload.requests)
              ? payload.requests
              : [];

        setRequests(rows);

        setSelectedId((currentId) => {
          if (currentId && rows.some((request) => request.id === currentId)) {
            return currentId;
          }

          return rows[0]?.id ?? null;
        });
      } catch (err) {
        console.error("Failed to load OSS join requests:", err);

        setError(
          err.message || t("errors.load", "Could not load OSS join requests."),
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [getAccessTokenSilently, t],
  );

  React.useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  /* =====================================================
     SELECTED REQUEST
     ===================================================== */

  const selectedRequest =
    requests.find((request) => request.id === selectedId) || null;

  /* =====================================================
     SUMMARY
     ===================================================== */

  const summary = React.useMemo(
    () => ({
      pending: requests.filter((request) => request.status === "PENDING")
        .length,

      approved: requests.filter(
        (request) =>
          request.status === "APPROVED" || request.status === "CODE_SENT",
      ).length,

      completed: requests.filter((request) => request.status === "COMPLETED")
        .length,

      rejected: requests.filter(
        (request) =>
          request.status === "REJECTED" || request.status === "EXPIRED",
      ).length,
    }),
    [requests],
  );

  /* =====================================================
     FILTER / ORDER
     ===================================================== */

  const filteredRequests = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    const filtered = requests.filter((request) => {
      if (statusFilter !== "ALL" && request.status !== statusFilter) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const searchable = [
        request.company_name,
        request.first_name,
        request.last_name,
        request.email,
        request.phone,
        request.role_in_company,
        request.country_code,
        ...getCoverage(request),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedQuery);
    });

    return [...filtered].sort((a, b) => {
      if (ordering === "OLDEST") {
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      }

      if (ordering === "COMPANY_ASC") {
        return (a.company_name || "").localeCompare(b.company_name || "");
      }

      if (ordering === "COMPANY_DESC") {
        return (b.company_name || "").localeCompare(a.company_name || "");
      }

      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [requests, query, statusFilter, ordering]);

  /* =====================================================
     MODAL KEYBOARD BEHAVIOUR

     IMPORTANT:
     We intentionally DO NOT set body overflow:hidden.
     The main page remains scrollable while a modal is open.
     ===================================================== */

  React.useEffect(() => {
    if (!approveModalOpen && !rejectModalOpen) {
      return undefined;
    }

    function handleEscape(event) {
      if (event.key !== "Escape" || actionLoading) {
        return;
      }

      setApproveModalOpen(false);
      setRejectModalOpen(false);
    }

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [approveModalOpen, rejectModalOpen, actionLoading]);

  /* =====================================================
     MODAL HELPERS
     ===================================================== */

  function openApproveModal() {
    if (!selectedRequest) return;

    setApprovalMessage(selectedRequest.approval_message || "");

    setApproveModalOpen(true);
  }

  function closeApproveModal() {
    if (actionLoading) return;

    setApproveModalOpen(false);
    setApprovalMessage("");
  }

  function openRejectModal() {
    if (!selectedRequest) return;

    setRejectionReason("");
    setRejectionNote("");
    setRejectModalOpen(true);
  }

  function closeRejectModal() {
    if (actionLoading) return;

    setRejectModalOpen(false);
    setRejectionReason("");
    setRejectionNote("");
  }

  /* =====================================================
     UPDATE ONE REQUEST
     ===================================================== */

  function replaceRequest(requestId, nextRequest) {
    setRequests((current) =>
      current.map((request) =>
        request.id === requestId
          ? {
              ...request,
              ...nextRequest,
            }
          : request,
      ),
    );
  }

  /* =====================================================
     APPROVE
     ===================================================== */

  async function approveRequest() {
    if (!selectedRequest || actionLoading) {
      return;
    }

    try {
      setActionLoading(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/admin/oss-join-requests/${selectedRequest.id}/approve/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            approval_message: approvalMessage.trim(),
          }),
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload.detail ||
            t("errors.approve", "Could not approve this OSS join request."),
        );
      }

      const updatedRequest = payload.request ||
        payload.oss_join_request || {
          status: payload.status || "APPROVED",
          approval_message: approvalMessage.trim(),
        };

      replaceRequest(selectedRequest.id, updatedRequest);

      closeApproveModal();

      showToast({
        type: "success",
        title: t("toast.approvedTitle", "Request approved"),
        message:
          payload.message ||
          t(
            "toast.approvedMessage",
            "{{organisation}} has been approved for OSS onboarding.",
            {
              organisation: selectedRequest.company_name,
            },
          ),
      });

      scrollSmoothlyToTop();
    } catch (err) {
      showToast({
        type: "error",
        title: t("toast.actionFailed", "Action failed"),
        message:
          err.message ||
          t("errors.approve", "Could not approve this OSS join request."),
      });
    } finally {
      setActionLoading(false);
    }
  }

  /* =====================================================
     REJECT
     ===================================================== */

  async function rejectRequest() {
    if (!selectedRequest || actionLoading) {
      return;
    }

    if (!rejectionReason) {
      showToast({
        type: "error",
        title: t("reject.reasonRequiredTitle", "Reason required"),
        message: t(
          "reject.reasonRequiredText",
          "Select a rejection reason before continuing.",
        ),
      });

      return;
    }

    if (!rejectionNote.trim()) {
      showToast({
        type: "error",
        title: t("reject.noteRequiredTitle", "Explanation required"),
        message: t(
          "reject.noteRequiredText",
          "Provide a short explanation for the applicant.",
        ),
      });

      return;
    }

    try {
      setActionLoading(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(
        `${API_BASE}/api/admin/oss-join-requests/${selectedRequest.id}/reject/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason_code: rejectionReason,
            rejection_reason: rejectionNote.trim(),
          }),
        },
      );

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload.detail ||
            t("errors.reject", "Could not reject this OSS join request."),
        );
      }

      const updatedRequest = payload.request ||
        payload.oss_join_request || {
          status: "REJECTED",
          rejection_reason: rejectionNote.trim(),
          rejection_reason_code: rejectionReason,
        };

      replaceRequest(selectedRequest.id, updatedRequest);

      closeRejectModal();

      showToast({
        type: "success",
        title: t("toast.rejectedTitle", "Request rejected"),
        message:
          payload.message ||
          t(
            "toast.rejectedMessage",
            "{{organisation}} has been notified of the decision.",
            {
              organisation: selectedRequest.company_name,
            },
          ),
      });

      scrollSmoothlyToTop();
    } catch (err) {
      showToast({
        type: "error",
        title: t("toast.actionFailed", "Action failed"),
        message:
          err.message ||
          t("errors.reject", "Could not reject this OSS join request."),
      });
    } finally {
      setActionLoading(false);
    }
  }

  /* =====================================================
     RENDER
     ===================================================== */

  return (
    <main className="admin-requests-page">
      {/* =================================================
          HEADER
          ================================================= */}

      <header className="admin-requests-header">
        <div>
          <span className="admin-requests-header__eyebrow">
            {t("header.eyebrow", "OSS ONBOARDING")}
          </span>

          <h1>{t("header.title", "Join Requests")}</h1>

          <p>
            {t(
              "header.subtitle",
              "Review organisations requesting access to LEG-UP as One-Stop Shops.",
            )}
          </p>
        </div>

        <div className="admin-requests-header__summary">
          <div>
            <span>{t("summary.pending", "Pending")}</span>

            <strong>{summary.pending}</strong>
          </div>

          <div>
            <span>{t("summary.onboarding", "Approved")}</span>

            <strong>{summary.approved}</strong>
          </div>

          <div>
            <span>{t("summary.completed", "Completed")}</span>

            <strong>{summary.completed}</strong>
          </div>
        </div>
      </header>

      {/* =================================================
          TOOLBAR
          ================================================= */}

      <section className="admin-requests-toolbar">
        <div className="admin-requests-search">
          <Search size={17} />

          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(
              "filters.searchPlaceholder",
              "Search organisation, applicant, email or postal code...",
            )}
          />

          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label={t("filters.clearSearch", "Clear search")}
            >
              <X size={15} />
            </button>
          ) : null}
        </div>

        <div className="admin-requests-toolbar__selects">
          <label className="admin-requests-toolbar-select">
            <span>{t("filters.status", "Status")}</span>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`statusOptions.${option.key}`, option.fallback)}
                </option>
              ))}
            </select>
          </label>

          <label className="admin-requests-toolbar-select">
            <span>{t("filters.ordering", "Order")}</span>

            <select
              value={ordering}
              onChange={(event) => setOrdering(event.target.value)}
            >
              {ORDERING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(`ordering.${option.key}`, option.fallback)}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="admin-requests-refresh"
            onClick={() =>
              loadRequests({
                background: true,
              })
            }
            disabled={refreshing}
            aria-label={t("actions.refresh", "Refresh requests")}
          >
            <RefreshCw
              size={17}
              className={refreshing ? "admin-requests-spinner" : ""}
            />
          </button>
        </div>
      </section>

      {/* =================================================
          ERROR
          ================================================= */}

      {error ? (
        <div className="admin-requests-error">
          <AlertTriangle size={18} />

          <span>{error}</span>

          <button type="button" onClick={() => loadRequests()}>
            {t("actions.retry", "Try again")}
          </button>
        </div>
      ) : null}

      {/* =================================================
          WORKSPACE
          ================================================= */}

      <section className="admin-requests-workspace">
        {/* ===============================================
            LIST
            =============================================== */}

        <section className="admin-requests-list-card">
          <header className="admin-requests-card-header">
            <div className="admin-requests-card-header__icon">
              <Clock3 size={18} />
            </div>

            <div>
              <h2>{t("list.title", "Incoming Join Requests")}</h2>

              <p>
                {t(
                  "list.subtitle",
                  "{{count}} requests match the current filters.",
                  {
                    count: filteredRequests.length,
                  },
                )}
              </p>
            </div>
          </header>

          <div className="admin-requests-list">
            {loading ? (
              <RequestsLoading />
            ) : filteredRequests.length ? (
              filteredRequests.map((request, index) => (
                <div
                  key={request.id}
                  className="admin-requests-row-animation"
                  style={{
                    "--request-delay": `${index * 45}ms`,
                  }}
                >
                  <RequestRow
                    request={request}
                    selected={request.id === selectedId}
                    locale={locale}
                    t={t}
                    onSelect={setSelectedId}
                  />
                </div>
              ))
            ) : (
              <div className="admin-requests-list-empty">
                <Search size={25} />

                <strong>{t("list.emptyTitle", "No requests found")}</strong>

                <span>
                  {requests.length
                    ? t(
                        "list.emptyFilteredText",
                        "Try changing the search term or filters.",
                      )
                    : t(
                        "list.emptyText",
                        "No OSS join requests have been submitted yet.",
                      )}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ===============================================
            DETAILS
            =============================================== */}

        <aside className="admin-requests-details-card">
          {selectedRequest ? (
            <>
              <header className="admin-requests-details-header">
                <div className="admin-requests-details-header__identity">
                  <span className="admin-requests-details-header__logo">
                    {getInitials(selectedRequest.company_name)}
                  </span>

                  <div>
                    <h2>{selectedRequest.company_name}</h2>

                    <span>
                      {t("details.requestReference", "Request #{{id}}", {
                        id: selectedRequest.id,
                      })}
                    </span>
                  </div>
                </div>

                <StatusBadge status={selectedRequest.status} t={t} />
              </header>

              <div className="admin-requests-details-body">
                {/* =======================================
                    REQUEST DETAILS
                    ======================================= */}

                <section className="admin-requests-detail-section">
                  <h3>{t("details.requestDetails", "Request Details")}</h3>

                  <div className="admin-requests-detail-list">
                    <DetailRow
                      icon={UserRound}
                      label={t("details.applicant", "Applicant")}
                    >
                      {getApplicantName(selectedRequest)}
                    </DetailRow>

                    <DetailRow
                      icon={BriefcaseBusiness}
                      label={t("details.role", "Role in company")}
                    >
                      {selectedRequest.role_in_company}
                    </DetailRow>

                    <DetailRow icon={Mail} label={t("details.email", "Email")}>
                      {selectedRequest.email}
                    </DetailRow>

                    <DetailRow icon={Phone} label={t("details.phone", "Phone")}>
                      {selectedRequest.phone}
                    </DetailRow>

                    <DetailRow
                      icon={Globe2}
                      label={t("details.country", "Country")}
                    >
                      {formatCountry(selectedRequest.country_code, t)}
                    </DetailRow>

                    <DetailRow
                      icon={MapPin}
                      label={t("details.coverage", "Postal codes covered")}
                    >
                      {formatCoverage(selectedRequest, t)}
                    </DetailRow>

                    <DetailRow
                      icon={Wrench}
                      label={t("details.ossType", "OSS type")}
                    >
                      {getOssType(selectedRequest, t)}
                    </DetailRow>

                    <DetailRow
                      icon={Clock3}
                      label={t("details.experience", "Years of experience")}
                    >
                      {t("details.years", "{{count}} years", {
                        count: selectedRequest.years_of_experience ?? 0,
                      })}
                    </DetailRow>

                    <DetailRow
                      icon={CalendarDays}
                      label={t("details.submitted", "Submitted")}
                    >
                      {formatDateTime(selectedRequest.created_at, locale)}
                    </DetailRow>
                  </div>

                  {selectedRequest.website_url ? (
                    <a
                      className="admin-requests-website-link"
                      href={selectedRequest.website_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink size={14} />

                      {t("details.openWebsite", "Open organisation website")}
                    </a>
                  ) : null}
                </section>

                {/* =======================================
                    MESSAGE
                    ======================================= */}

                <section className="admin-requests-detail-section">
                  <h3>
                    {t("details.messageTitle", "Message from organisation")}
                  </h3>

                  <div className="admin-requests-message">
                    <MessageSquareText size={18} />

                    <p>
                      {selectedRequest.message ||
                        t("common.noMessage", "No message provided.")}
                    </p>
                  </div>
                </section>

                {/* =======================================
                    REJECTION
                    ======================================= */}

                {selectedRequest.status === "REJECTED" &&
                selectedRequest.rejection_reason ? (
                  <section className="admin-requests-detail-section">
                    <h3>
                      {t("details.rejectionTitle", "Rejection information")}
                    </h3>

                    <div className="admin-requests-rejection-note">
                      <CircleX size={18} />

                      <div>
                        {selectedRequest.rejection_reason_code ? (
                          <strong>
                            {getRejectionReasonLabel(
                              selectedRequest.rejection_reason_code,
                              t,
                            )}
                          </strong>
                        ) : null}

                        <p>{selectedRequest.rejection_reason}</p>
                      </div>
                    </div>
                  </section>
                ) : null}

                {/* =======================================
                    APPROVAL MESSAGE
                    ======================================= */}

                {selectedRequest.approval_message ? (
                  <section className="admin-requests-detail-section">
                    <h3>
                      {t("details.approvalMessageTitle", "Approval message")}
                    </h3>

                    <div className="admin-requests-message">
                      <CheckCircle2 size={18} />

                      <p>{selectedRequest.approval_message}</p>
                    </div>
                  </section>
                ) : null}

                {/* =======================================
                    AFTER APPROVAL
                    ======================================= */}

                <section className="admin-requests-after-approval">
                  <span>
                    <ShieldCheck size={19} />
                  </span>

                  <div>
                    <strong>
                      {t(
                        "details.afterApprovalTitle",
                        "What happens after approval?",
                      )}
                    </strong>

                    <p>
                      {t(
                        "details.afterApprovalText",
                        "The organisation receives its OSS access code and can continue the remaining LEG-UP onboarding steps.",
                      )}
                    </p>
                  </div>
                </section>
              </div>

              {/* =========================================
                  ACTIONS
                  ========================================= */}

              {selectedRequest.status === "PENDING" ? (
                <footer className="admin-requests-details-actions">
                  <button
                    type="button"
                    className="admin-requests-action admin-requests-action--approve"
                    onClick={openApproveModal}
                  >
                    <Check size={16} />

                    {t("actions.approve", "Approve request")}
                  </button>

                  <button
                    type="button"
                    className="admin-requests-action admin-requests-action--reject"
                    onClick={openRejectModal}
                  >
                    <X size={16} />

                    {t("actions.reject", "Reject request")}
                  </button>
                </footer>
              ) : null}
            </>
          ) : (
            <EmptyDetails t={t} />
          )}
        </aside>
      </section>

      {/* =================================================
          APPROVE MODAL
          ================================================= */}

      {approveModalOpen && selectedRequest
        ? createPortal(
            <div
              className="admin-requests-modal-backdrop"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !actionLoading) {
                  closeApproveModal();
                }
              }}
            >
              <section
                className="admin-requests-modal admin-requests-modal--approve"
                role="dialog"
                aria-modal="true"
              >
                <button
                  type="button"
                  className="admin-requests-modal__close"
                  onClick={closeApproveModal}
                  disabled={actionLoading}
                  aria-label={t("common.close", "Close")}
                >
                  <X size={18} />
                </button>

                <span className="admin-requests-modal__main-icon is-success">
                  <BadgeCheck size={25} />
                </span>

                <h2>{t("approve.title", "Approve OSS request?")}</h2>

                <p className="admin-requests-modal__intro">
                  {t(
                    "approve.subtitle",
                    "Approve this organisation to continue the LEG-UP OSS onboarding process.",
                  )}
                </p>

                <div className="admin-requests-modal__organisation">
                  <span>{getInitials(selectedRequest.company_name)}</span>

                  <div>
                    <strong>{selectedRequest.company_name}</strong>

                    <small>{getApplicantName(selectedRequest)}</small>
                  </div>
                </div>

                <label className="admin-requests-field admin-requests-field--full">
                  <span>{t("approve.messageLabel", "Approval message")}</span>

                  <textarea
                    value={approvalMessage}
                    onChange={(event) => setApprovalMessage(event.target.value)}
                    maxLength={600}
                    placeholder={t(
                      "approve.messagePlaceholder",
                      "Optional message for the applicant...",
                    )}
                  />

                  <small>
                    {approvalMessage.length}
                    /600
                  </small>
                </label>

                <div className="admin-requests-modal__info is-success">
                  <CheckCircle2 size={17} />

                  <span>
                    {t(
                      "approve.info",
                      "The applicant will be notified and the OSS access-code onboarding flow can continue.",
                    )}
                  </span>
                </div>

                <div className="admin-requests-modal__actions">
                  <button
                    type="button"
                    className="admin-requests-modal__cancel"
                    onClick={closeApproveModal}
                    disabled={actionLoading}
                  >
                    {t("actions.cancel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className="admin-requests-modal__confirm admin-requests-modal__confirm--approve"
                    onClick={approveRequest}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <>
                        <RefreshCw
                          size={16}
                          className="admin-requests-spinner"
                        />

                        {t("actions.approving", "Approving...")}
                      </>
                    ) : (
                      <>
                        <Check size={16} />

                        {t("actions.approve", "Approve request")}
                      </>
                    )}
                  </button>
                </div>
              </section>
            </div>,
            document.body,
          )
        : null}

      {/* =================================================
          REJECT MODAL
          ================================================= */}

      {rejectModalOpen && selectedRequest
        ? createPortal(
            <div
              className="admin-requests-modal-backdrop"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget && !actionLoading) {
                  closeRejectModal();
                }
              }}
            >
              <section
                className="admin-requests-modal admin-requests-modal--reject"
                role="dialog"
                aria-modal="true"
              >
                <button
                  type="button"
                  className="admin-requests-modal__close"
                  onClick={closeRejectModal}
                  disabled={actionLoading}
                  aria-label={t("common.close", "Close")}
                >
                  <X size={18} />
                </button>

                <span className="admin-requests-modal__main-icon is-danger">
                  <AlertTriangle size={25} />
                </span>

                <h2>{t("reject.title", "Reject OSS request?")}</h2>

                <p className="admin-requests-modal__intro">
                  {t(
                    "reject.subtitle",
                    "Provide a clear reason. The decision and explanation will be shared with the applicant.",
                  )}
                </p>

                <div className="admin-requests-modal__organisation">
                  <span>{getInitials(selectedRequest.company_name)}</span>

                  <div>
                    <strong>{selectedRequest.company_name}</strong>

                    <small>{getApplicantName(selectedRequest)}</small>
                  </div>
                </div>

                <label className="admin-requests-field admin-requests-field--full">
                  <span>
                    {t("reject.reasonLabel", "Reason category")}
                    <b>*</b>
                  </span>

                  <select
                    value={rejectionReason}
                    onChange={(event) => setRejectionReason(event.target.value)}
                  >
                    <option value="">
                      {t("reject.reasonPlaceholder", "Select a reason...")}
                    </option>

                    {REJECTION_REASON_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(`rejectionReasons.${option.key}`, option.fallback)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="admin-requests-field admin-requests-field--full">
                  <span>
                    {t("reject.noteLabel", "Explanation")}
                    <b>*</b>
                  </span>

                  <textarea
                    value={rejectionNote}
                    onChange={(event) => setRejectionNote(event.target.value)}
                    maxLength={600}
                    placeholder={t(
                      "reject.notePlaceholder",
                      "Explain clearly why the request cannot be approved.",
                    )}
                  />

                  <small>
                    {rejectionNote.length}
                    /600
                  </small>
                </label>

                <div className="admin-requests-modal__info is-warning">
                  <AlertTriangle size={17} />

                  <span>
                    {t(
                      "reject.info",
                      "The applicant will receive this decision and the explanation you provide.",
                    )}
                  </span>
                </div>

                <div className="admin-requests-modal__actions">
                  <button
                    type="button"
                    className="admin-requests-modal__cancel"
                    onClick={closeRejectModal}
                    disabled={actionLoading}
                  >
                    {t("actions.cancel", "Cancel")}
                  </button>

                  <button
                    type="button"
                    className="admin-requests-modal__confirm admin-requests-modal__confirm--reject"
                    onClick={rejectRequest}
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <>
                        <RefreshCw
                          size={16}
                          className="admin-requests-spinner"
                        />

                        {t("actions.rejecting", "Rejecting...")}
                      </>
                    ) : (
                      <>
                        <X size={16} />

                        {t("actions.reject", "Reject request")}
                      </>
                    )}
                  </button>
                </div>
              </section>
            </div>,
            document.body,
          )
        : null}
    </main>
  );
}
